import { action, observable } from 'mobx';
import { observer } from 'mobx-react';
import { autoinject, inject } from 'Mri.Web.Common';
import * as React from 'react';
import { SeqEvent, SeqLogsStore } from './SeqLogsStore';

const LOG_LEVELS = ['All', 'Verbose', 'Debug', 'Information', 'Warning', 'Error', 'Fatal'];
const TIME_RANGES = [
    { value: '5m', label: 'Last 5 minutes' },
    { value: '15m', label: 'Last 15 minutes' },
    { value: '1h', label: 'Last 1 hour' },
    { value: '6h', label: 'Last 6 hours' },
    { value: '24h', label: 'Last 24 hours' },
];

@autoinject
@observer
export class SeqLogsDashboard extends React.Component {
    public async componentDidMount() {
        await this.seqLogsStore.loadEventsAsync();
        if (this.seqLogsStore.autoRefresh) {
            this.startPolling();
        }

        this.scrollContainer = document.querySelector('.main-container');
        if (this.scrollContainer) {
            this.scrollContainer.addEventListener('scroll', this.handleScroll);
        }
    }

    public componentWillUnmount() {
        this.stopPolling();
        if (this.scrollContainer) {
            this.scrollContainer.removeEventListener('scroll', this.handleScroll);
        }
    }

    @observable
    private expandedEventId: string | null = null;

    @observable
    private filterInput = '';

    private pollInterval: number | null = null;
    private scrollContainer: HTMLElement | null = null;

    /**
     * The credential line, at the top of the page: was a read API key used, and did the read succeed.
     * It renders on success too, deliberately. An empty event list has two very different causes - "nothing
     * was logged in this window" and "Seq refused us" - and prd spent a long time showing the second while
     * looking like the first, because a 401 reached the screen as an empty list and nothing else.
     */
    private renderCredentialStatus() {
        const status = this.seqLogsStore.status;
        if (!status) {
            return null;
        }

        const keyText = status.apiKeyUsed
            ? `read API key used (${status.apiKeyLength} chars)`
            : status.apiKeyConfigured
                ? 'read API key configured but NOT sent'
                : 'no read API key configured';
        const httpText = status.httpStatusCode ? ` (HTTP ${status.httpStatusCode})` : '';
        const resultText = status.succeeded ? `query succeeded${httpText}` : `query FAILED${httpText}`;

        return (
            <div
                className={`alert ${status.succeeded ? 'alert-secondary' : 'alert-warning'} py-2 px-3 small mb-3`}
                role="alert"
            >
                <div>
                    <strong>Seq:</strong> {keyText} - {resultText}
                    {status.seqServer ? (
                        <>
                            {' against '}
                            <code>{status.seqServer}</code>
                        </>
                    ) : null}
                </div>
                {status.message ? <div className="mt-1">{status.message}</div> : null}
            </div>
        );
    }

    public render() {
        const { events, loading, level, timeRange, autoRefresh } = this.seqLogsStore;

        return (
            <div className="container-fluid mt-3">
                {this.renderCredentialStatus()}

                <div className="card mb-3" style={{ position: 'sticky', top: 0, zIndex: 10 }}>
                    <div className="card-header d-flex justify-content-between align-items-center">
                        <span>Filters</span>
                        <div className="d-flex align-items-center gap-2">
                            <div className="form-check form-switch mb-0">
                                <input
                                    className="form-check-input"
                                    type="checkbox"
                                    id="autoRefreshToggle"
                                    checked={autoRefresh}
                                    onChange={this.handleToggleAutoRefresh}
                                />
                                <label className="form-check-label small" htmlFor="autoRefreshToggle">
                                    Auto-refresh
                                </label>
                            </div>
                            <button
                                className="btn btn-sm btn-outline-secondary"
                                onClick={this.handleRefresh}
                                disabled={loading}
                            >
                                {loading ? 'Loading...' : 'Refresh'}
                            </button>
                        </div>
                    </div>
                    <div className="card-body">
                        <div className="row g-2">
                            <div className="col-md-6">
                                <input
                                    type="text"
                                    className="form-control form-control-sm"
                                    placeholder="Search logs (text or Seq filter expression)..."
                                    value={this.filterInput}
                                    onChange={this.handleFilterChange}
                                    onKeyDown={this.handleFilterKeyDown}
                                />
                            </div>
                            <div className="col-md-3">
                                <select
                                    className="form-select form-select-sm"
                                    value={level}
                                    onChange={this.handleLevelChange}
                                >
                                    {LOG_LEVELS.map(l => (
                                        <option key={l} value={l}>{l}</option>
                                    ))}
                                </select>
                            </div>
                            <div className="col-md-3">
                                <select
                                    className="form-select form-select-sm"
                                    value={timeRange}
                                    onChange={this.handleTimeRangeChange}
                                >
                                    {TIME_RANGES.map(t => (
                                        <option key={t.value} value={t.value}>{t.label}</option>
                                    ))}
                                </select>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="card">
                    <div className="card-header">
                        Events ({events.length})
                    </div>
                    <div className="card-body p-0">
                        <div className="table-responsive">
                            <table className="table table-sm table-striped mb-0">
                                <thead>
                                    <tr>
                                        <th style={{ width: '180px' }}>Timestamp</th>
                                        <th style={{ width: '100px' }}>Level</th>
                                        <th style={{ width: '140px' }}>Application</th>
                                        <th>Message</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {events.length === 0 && !loading && (
                                        <tr>
                                            <td colSpan={4} className="text-center text-muted py-3">
                                                No events found
                                            </td>
                                        </tr>
                                    )}
                                    {events.map(event => (
                                        <React.Fragment key={event.id}>
                                            <tr
                                                onClick={() => this.toggleExpand(event.id)}
                                                style={{ cursor: 'pointer' }}
                                            >
                                                <td className="text-nowrap">
                                                    <small>{new Date(event.timestamp).toLocaleString()}</small>
                                                </td>
                                                <td>{this.renderLevelBadge(event.level)}</td>
                                                <td>
                                                    <small className="text-muted">
                                                        {event.properties?.Application ?? event.properties?.Role ?? '-'}
                                                    </small>
                                                </td>
                                                <td>
                                                    <small className={event.exception ? 'text-danger' : ''}>
                                                        {this.truncateMessage(event.renderedMessage)}
                                                    </small>
                                                </td>
                                            </tr>
                                            {this.expandedEventId === event.id && (
                                                <tr>
                                                    <td colSpan={4} className="p-3">
                                                        {this.renderEventDetail(event)}
                                                    </td>
                                                </tr>
                                            )}
                                        </React.Fragment>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                    {events.length > 0 && (
                        <div className="card-footer text-center">
                            {loading ? (
                                <span className="text-muted">Loading...</span>
                            ) : (
                                <button
                                    className="btn btn-sm btn-outline-primary"
                                    onClick={this.handleLoadMore}
                                >
                                    Load More
                                </button>
                            )}
                        </div>
                    )}
                </div>
            </div>
        );
    }

    private renderLevelBadge(level: string) {
        const badgeClasses: Record<string, string> = {
            Verbose: 'badge bg-secondary',
            Debug: 'badge bg-secondary',
            Information: 'badge bg-info text-dark',
            Warning: 'badge bg-warning text-dark',
            Error: 'badge bg-danger',
            Fatal: 'badge bg-danger',
        };
        return <span className={badgeClasses[level] ?? 'badge bg-secondary'}>{level}</span>;
    }

    private truncateMessage(message: string | null) {
        if (!message) return '-';
        return message.length > 200 ? message.substring(0, 200) + '...' : message;
    }

    private renderEventDetail(event: SeqEvent) {
        return (
            <div>
                <h6 className="mb-2">Full Message</h6>
                <pre className="p-2 rounded border mb-3" style={{ whiteSpace: 'pre-wrap', fontSize: '0.8rem' }}>
                    {event.renderedMessage}
                </pre>

                {event.exception && (
                    <>
                        <h6 className="mb-2 text-danger">Exception</h6>
                        <pre className="p-2 rounded border mb-3 text-danger" style={{ whiteSpace: 'pre-wrap', fontSize: '0.8rem' }}>
                            {event.exception}
                        </pre>
                    </>
                )}

                {Object.keys(event.properties).length > 0 && (
                    <>
                        <h6 className="mb-2">Properties</h6>
                        <table className="table table-sm table-bordered mb-0" style={{ fontSize: '0.8rem' }}>
                            <tbody>
                                {Object.entries(event.properties).map(([key, value]) => (
                                    <tr key={key}>
                                        <td className="fw-bold" style={{ width: '200px' }}>{key}</td>
                                        <td><pre className="mb-0" style={{ whiteSpace: 'pre-wrap' }}>{typeof value === 'string' ? value : JSON.stringify(value, null, 2)}</pre></td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </>
                )}
            </div>
        );
    }

    @action
    private toggleExpand(eventId: string) {
        this.expandedEventId = this.expandedEventId === eventId ? null : eventId;
    }

    private handleScroll = () => {
        if (!this.scrollContainer || this.seqLogsStore.loading || this.seqLogsStore.events.length === 0) return;
        const { scrollTop, scrollHeight, clientHeight } = this.scrollContainer;
        if (scrollHeight - scrollTop - clientHeight < 200) {
            this.seqLogsStore.loadMoreAsync();
        }
    };

    private handleRefresh = async () => {
        await this.seqLogsStore.loadEventsAsync();
    };

    private handleLoadMore = async () => {
        await this.seqLogsStore.loadMoreAsync();
    };

    @action
    private handleFilterChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        this.filterInput = e.target.value;
    };

    private handleFilterKeyDown = async (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            this.seqLogsStore.setFilter(this.filterInput);
            await this.seqLogsStore.loadEventsAsync();
        }
    };

    private handleLevelChange = async (e: React.ChangeEvent<HTMLSelectElement>) => {
        this.seqLogsStore.setLevel(e.target.value);
        await this.seqLogsStore.loadEventsAsync();
    };

    private handleTimeRangeChange = async (e: React.ChangeEvent<HTMLSelectElement>) => {
        this.seqLogsStore.setTimeRange(e.target.value);
        await this.seqLogsStore.loadEventsAsync();
    };

    private handleToggleAutoRefresh = () => {
        this.seqLogsStore.toggleAutoRefresh();
        if (this.seqLogsStore.autoRefresh) {
            this.startPolling();
        } else {
            this.stopPolling();
        }
    };

    private startPolling() {
        this.stopPolling();
        if (this.seqLogsStore.autoRefresh) {
            this.pollInterval = window.setInterval(() => {
                if (!this.seqLogsStore.loading) {
                    this.seqLogsStore.loadEventsAsync();
                }
            }, 10000);
        }
    }

    private stopPolling() {
        if (this.pollInterval) {
            clearInterval(this.pollInterval);
            this.pollInterval = null;
        }
    }

    @inject
    private seqLogsStore!: SeqLogsStore;
}
